import { useCallback } from "react";

import { useTranslation } from "@/lib/i18n";
import MapControlLegend from "@/components/map/map-control-legend";
import MapControlTabs from "@/components/map/map-control-tabs";
import { useMapControlLegendState } from "@/components/map/use-map-control-legend-state";
import Icon from "@/components/icon/icon";
import { MapLayersController } from "@/lib/map-layers";
import EnumControl from "@/features/shared/widgets/map/controls/enum-control";
import LayersPanel from "@/features/shared/widgets/map/controls/layers-panel";
import { ConfigPanelStack } from "@/features/shared/widgets/map/map.styles";

type LayerControlProps = {
  mapLayersController: MapLayersController;
};

const LayerControl = ({ mapLayersController }: LayerControlProps) => {
  const { t } = useTranslation();
  const {
    activePanel,
    setActivePanel,
    selectedLegendLayerId,
    setSelectedLegendLayerId,
    hasLayersWithLegend,
    openLegend,
  } = useMapControlLegendState(mapLayersController.layers);

  const handleShowLegendForLayer = useCallback(
    (layerId: string) => openLegend(layerId),
    [openLegend],
  );

  return (
    <MapControlTabs.Root value={activePanel} onValueChange={setActivePanel}>
      <MapControlTabs.List>
        <MapControlTabs.Trigger value="config" aria-label={t("map.config_tab_label")}>
          <Icon name="layers" size={20} />
        </MapControlTabs.Trigger>
        {hasLayersWithLegend && (
          <MapControlTabs.Trigger value="legend" aria-label={t("map.legend_tab_label")}>
            <Icon name="info" size={20} />
          </MapControlTabs.Trigger>
        )}
      </MapControlTabs.List>

      <MapControlTabs.Content value="config">
        <ConfigPanelStack>
          <LayersPanel
            mapLayersController={mapLayersController}
            onShowLegendForLayer={handleShowLegendForLayer}
          />
          <EnumControl {...mapLayersController} />
        </ConfigPanelStack>
      </MapControlTabs.Content>
      {hasLayersWithLegend && (
        <MapControlTabs.Content value="legend">
          <MapControlLegend
            layers={mapLayersController.layers}
            selectedLegendLayerId={selectedLegendLayerId}
            onSelectedLegendLayerIdChange={setSelectedLegendLayerId}
          />
        </MapControlTabs.Content>
      )}
    </MapControlTabs.Root>
  );
};

export default LayerControl;
